import React, { useEffect, useRef, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FileInput,
	Group,
	Modal,
	NativeSelect,
	Stack,
	Tabs as MantineTabs,
	Text,
	Textarea,
	TextInput,
} from '@mantine/core'
import { uploadImageToS3 } from '../services/utils/uploadImageToS3'
import ImageCropperModal from './ImageCropperModal'
import { readFileAsDataUrl } from '../services/utils/imageCropper'
import { toast } from '../services/utils/toast'

const MAX_DESCRIPTION_LINES = 5
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const ITEM_IMAGE_CROP_PRESETS = {
	square: {
		key: 'square',
		label: 'Square',
		width: 350,
		height: 350,
	},
	wristband: {
		key: 'wristband',
		label: 'Wristband',
		width: 750,
		height: 150,
	},
}
const ITEM_IMAGE_DEFAULT_CROP_PRESET = ITEM_IMAGE_CROP_PRESETS.wristband.key
const ITEM_IMAGE_CROP_PRESET_OPTIONS = Object.values(ITEM_IMAGE_CROP_PRESETS)
const EXPERIENCE_STORE_IMAGE_WIDTH = 1600
const EXPERIENCE_STORE_IMAGE_HEIGHT = 900

const clampDescriptionLines = (value) => {
	const text = String(value || '')
	const lines = text.split('\n')
	return lines.length > MAX_DESCRIPTION_LINES
		? lines.slice(0, MAX_DESCRIPTION_LINES).join('\n')
		: text
}

const getItemImageCropPreset = (key) =>
	ITEM_IMAGE_CROP_PRESETS[key] ||
	ITEM_IMAGE_CROP_PRESETS[ITEM_IMAGE_DEFAULT_CROP_PRESET]

const ItemOptionModal = ({ show, onClose, onAdd, item }) => {
	const [name, setName] = useState('')
	const [allowAddToCheckIn, setAllowAddToCheckIn] = useState(true)
	const [showInExperienceStore, setShowInExperienceStore] = useState(false)
	const [listPrice, setListPrice] = useState('')
	const [totalUnitLimit, setTotalUnitLimit] = useState('')
	const [description, setDescription] = useState('')
	const [experienceStoreDescription, setExperienceStoreDescription] =
		useState('')
	const [file, setFile] = useState(null)
	const [selectedImageFile, setSelectedImageFile] = useState(null)
	const [preview, setPreview] = useState('')
	const [removeImage, setRemoveImage] = useState(false)
	const [experienceStoreImageFile, setExperienceStoreImageFile] = useState(null)
	const [
		selectedExperienceStoreImageFile,
		setSelectedExperienceStoreImageFile,
	] = useState(null)
	const [experienceStoreImagePreview, setExperienceStoreImagePreview] =
		useState('')
	const [removeExperienceStoreImage, setRemoveExperienceStoreImage] =
		useState(false)
	const [uploading, setUploading] = useState(false)
	const [error, setError] = useState('')
	const [fieldErrors, setFieldErrors] = useState({})
	const [cropModalOpened, setCropModalOpened] = useState(false)
	const [cropSourceUrl, setCropSourceUrl] = useState('')
	const [cropTarget, setCropTarget] = useState('item')
	const [cropPresetKey, setCropPresetKey] = useState(
		ITEM_IMAGE_DEFAULT_CROP_PRESET,
	)
	const descriptionRef = useRef(null)
	const localPreviewUrlRef = useRef('')
	const localExperienceStorePreviewUrlRef = useRef('')

	const clearLocalPreview = () => {
		if (!localPreviewUrlRef.current) return
		URL.revokeObjectURL(localPreviewUrlRef.current)
		localPreviewUrlRef.current = ''
	}

	const clearLocalExperienceStorePreview = () => {
		if (!localExperienceStorePreviewUrlRef.current) return
		URL.revokeObjectURL(localExperienceStorePreviewUrlRef.current)
		localExperienceStorePreviewUrlRef.current = ''
	}

	const clearCropState = () => {
		setCropModalOpened(false)
		setCropSourceUrl('')
		setCropTarget('item')
	}

	const clearErrors = () => {
		setError('')
		setFieldErrors({})
	}

	const setModalError = (message, fields = {}) => {
		setError(message)
		setFieldErrors(fields)
	}

	const setLocalPreview = (nextFile) => {
		clearLocalPreview()
		if (!nextFile) return
		const nextUrl = URL.createObjectURL(nextFile)
		localPreviewUrlRef.current = nextUrl
		setPreview(nextUrl)
	}

	const setLocalExperienceStorePreview = (nextFile) => {
		clearLocalExperienceStorePreview()
		if (!nextFile) return
		const nextUrl = URL.createObjectURL(nextFile)
		localExperienceStorePreviewUrlRef.current = nextUrl
		setExperienceStoreImagePreview(nextUrl)
	}

	useEffect(() => {
		if (!show) return

		clearErrors()
		setUploading(false)

		if (item) {
			setName(item.name || '')
			setAllowAddToCheckIn(item.allowAddToCheckIn !== false)
			setShowInExperienceStore(Boolean(item.showInExperienceStore))
			setListPrice(item.listPrice != null ? String(item.listPrice) : '')
			setTotalUnitLimit(
				item.totalUnitLimit != null ? String(item.totalUnitLimit) : '',
			)
			setDescription(clampDescriptionLines(item.description || ''))
			setExperienceStoreDescription(item.experienceStoreDescription || '')
			clearLocalPreview()
			setPreview(item.imageUrl || '')
			setFile(null)
			setSelectedImageFile(null)
			setRemoveImage(false)
			clearLocalExperienceStorePreview()
			setExperienceStoreImagePreview(item.experienceStoreImage || '')
			setExperienceStoreImageFile(null)
			setSelectedExperienceStoreImageFile(null)
			setRemoveExperienceStoreImage(false)
			setCropPresetKey(getItemImageCropPreset(item.imageCropPreset).key)
			clearCropState()
			return
		}

		setName('')
		setAllowAddToCheckIn(true)
		setShowInExperienceStore(false)
		setListPrice('')
		setTotalUnitLimit('')
		setDescription('')
		setExperienceStoreDescription('')
		clearLocalPreview()
		setPreview('')
		setFile(null)
		setSelectedImageFile(null)
		setRemoveImage(false)
		clearLocalExperienceStorePreview()
		setExperienceStoreImagePreview('')
		setExperienceStoreImageFile(null)
		setSelectedExperienceStoreImageFile(null)
		setRemoveExperienceStoreImage(false)
		setCropPresetKey(ITEM_IMAGE_DEFAULT_CROP_PRESET)
		clearCropState()
	}, [item, show])

	const activeCropPreset = getItemImageCropPreset(cropPresetKey)
	const isRecropAllowed = Boolean(file && preview)
	const isExperienceStoreRecropAllowed = Boolean(
		experienceStoreImageFile && experienceStoreImagePreview,
	)

	useEffect(
		() => () => {
			clearLocalPreview()
			clearLocalExperienceStorePreview()
		},
		[],
	)

	const handleInsertBullet = () => {
		const textarea = descriptionRef.current
		const start = textarea?.selectionStart ?? description.length
		const end = textarea?.selectionEnd ?? description.length
		const needsLeadingNewline = start > 0 && description[start - 1] !== '\n'
		const bulletPrefix = needsLeadingNewline ? '\n- ' : '- '
		const nextValue =
			description.slice(0, start) + bulletPrefix + description.slice(end)
		const clampedValue = clampDescriptionLines(nextValue)

		setDescription(clampedValue)

		window.requestAnimationFrame(() => {
			if (!textarea) return
			const cursor = Math.min(start + bulletPrefix.length, clampedValue.length)
			textarea.focus()
			textarea.setSelectionRange(cursor, cursor)
		})
	}

	const handleFileChange = async (selected) => {
		if (!selected) return

		setSelectedImageFile(selected)
		try {
			if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) {
				throw new Error('Only JPG, PNG, or WebP images are allowed.')
			}
			const sourceUrl = await readFileAsDataUrl(selected)
			setCropTarget('item')
			setCropSourceUrl(sourceUrl)
			setCropModalOpened(true)
		} catch (error) {
			setModalError(error.message || 'Failed to load image for cropping.', {
				image: error.message || 'Failed to load image for cropping.',
			})
		} finally {
			setSelectedImageFile(null)
		}
	}

	const handleRecropPreview = () => {
		if (!preview || !isRecropAllowed) return
		setCropTarget('item')
		setCropSourceUrl(preview)
		setCropModalOpened(true)
	}

	const handleExperienceStoreImageChange = async (selected) => {
		if (!selected) return

		setSelectedExperienceStoreImageFile(selected)
		try {
			if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) {
				throw new Error('Only JPG, PNG, or WebP images are allowed.')
			}
			const sourceUrl = await readFileAsDataUrl(selected)
			setCropTarget('experienceStore')
			setCropSourceUrl(sourceUrl)
			setCropModalOpened(true)
		} catch (error) {
			setModalError(error.message || 'Failed to load image for cropping.', {
				experienceStoreImage:
					error.message || 'Failed to load image for cropping.',
			})
		} finally {
			setSelectedExperienceStoreImageFile(null)
		}
	}

	const handleRecropExperienceStoreImage = () => {
		if (!experienceStoreImagePreview || !isExperienceStoreRecropAllowed) return
		setCropTarget('experienceStore')
		setCropSourceUrl(experienceStoreImagePreview)
		setCropModalOpened(true)
	}

	const handleRemoveExperienceStoreImage = () => {
		clearLocalExperienceStorePreview()
		setExperienceStoreImagePreview('')
		setExperienceStoreImageFile(null)
		setRemoveExperienceStoreImage(true)
		clearCropState()
		setSelectedExperienceStoreImageFile(null)
	}

	const handleRemoveImage = () => {
		clearLocalPreview()
		setPreview('')
		setFile(null)
		setRemoveImage(true)
		clearCropState()
		setSelectedImageFile(null)
	}

	const handleApplyCrop = async (blob) => {
		if (!blob) return
		if (cropTarget === 'experienceStore') {
			const nextFile = new File(
				[blob],
				`experience-store-item-${Date.now()}.jpg`,
				{ type: 'image/jpeg' },
			)
			setExperienceStoreImageFile(nextFile)
			setLocalExperienceStorePreview(nextFile)
			setRemoveExperienceStoreImage(false)
			clearCropState()
			return
		}
		const nextFile = new File([blob], `item-option-${Date.now()}.jpg`, {
			type: 'image/jpeg',
		})
		setFile(nextFile)
		setLocalPreview(nextFile)
		setRemoveImage(false)
		clearCropState()
	}

	const handleSubmit = async () => {
		const trimmedName = name.trim()

		if (!trimmedName) {
			setModalError('Add an item option name before saving.', {
				name: 'Name is required.',
			})
			return
		}
		if (allowAddToCheckIn && listPrice) {
			const parsed = Number.parseFloat(listPrice)
			if (!Number.isFinite(parsed) || parsed < 0) {
				setModalError('List price must be a valid non-negative number.', {
					listPrice: 'Enter a valid non-negative number.',
				})
				return
			}
		}
		if (showInExperienceStore) {
			const parsed = Number.parseFloat(listPrice)
			if (!allowAddToCheckIn || !Number.isFinite(parsed) || parsed < 0.5) {
				setModalError(
					'Store items must be available to check-ins and cost at least $0.50.',
					{ listPrice: 'Enter a price of at least $0.50.' },
				)
				return
			}
		}
		const normalizedTotalUnitLimit = totalUnitLimit.trim()
		let nextTotalUnitLimit = null
		if (normalizedTotalUnitLimit) {
			if (!/^\d+$/.test(normalizedTotalUnitLimit)) {
				setModalError('Total Unit Limit must be a whole number of 0 or more.', {
					totalUnitLimit: 'Enter a whole number of 0 or more.',
				})
				return
			}
			nextTotalUnitLimit = Number.parseInt(normalizedTotalUnitLimit, 10)
		}

		clearErrors()
		setUploading(true)
		try {
			let imageUrl = item?.imageUrl || null
			if (file) {
				imageUrl = await uploadImageToS3(file)
			} else if (removeImage) {
				imageUrl = null
			}
			let experienceStoreImage = item?.experienceStoreImage || null
			if (experienceStoreImageFile) {
				experienceStoreImage = await uploadImageToS3(experienceStoreImageFile)
			} else if (removeExperienceStoreImage) {
				experienceStoreImage = null
			}

			await onAdd({
				name: trimmedName,
				allowAddToCheckIn,
				showInExperienceStore,
				imageCropPreset: cropPresetKey,
				imageUrl,
				experienceStoreImage,
				experienceStoreDescription:
					experienceStoreDescription.trim() || null,
				description: description.trim() || null,
				listPrice:
					allowAddToCheckIn && listPrice.trim() ? listPrice.trim() : null,
				totalUnitLimit: nextTotalUnitLimit,
			})
			toast.success({
				title: item ? 'Item option updated' : 'Item option added',
				message: `${trimmedName} was saved successfully.`,
			})
		} catch (err) {
			console.error('Error saving item option', err)
			setModalError(err.message || 'Failed to save item option.')
		} finally {
			setUploading(false)
		}
	}

	return (
		<Modal
			opened={show}
			onClose={onClose}
			title={`${item ? 'Edit' : 'Add'} Item Option`}
			size='lg'
			centered
			closeOnClickOutside={!cropModalOpened}
			closeOnEscape={!cropModalOpened}
		>
			<Stack spacing='md'>
				{error && (
					<Alert color='red' title='Item option not saved'>
						{error}
					</Alert>
				)}

				<MantineTabs defaultValue='details' radius='md'>
					<MantineTabs.List grow mb='md'>
						<MantineTabs.Tab value='details'>Details</MantineTabs.Tab>
						<MantineTabs.Tab value='inventory'>Inventory</MantineTabs.Tab>
					</MantineTabs.List>

					<MantineTabs.Panel value='details'>
						<Stack spacing='md'>
							<TextInput
								label='Name'
								value={name}
								error={fieldErrors.name}
								onChange={(e) => {
									setName(e.target.value)
									if (error) clearErrors()
								}}
							/>

							<Box>
								<Group position='apart' align='center' mb={6}>
									<Text size='sm' weight={500}>
										Description
									</Text>
									<Button
										variant='outline'
										size='xs'
										onClick={handleInsertBullet}
									>
										Add Bullet
									</Button>
								</Group>
								<Textarea
									minRows={5}
									ref={descriptionRef}
									placeholder='Short description for this item'
									value={description}
									onChange={(e) => {
										if (error) clearErrors()
										setDescription(clampDescriptionLines(e.target.value))
									}}
								/>
							</Box>

							<Box>
								<Text size='sm' weight={500} mb={6}>
									Image ({activeCropPreset.width}px by{' '}
									{activeCropPreset.height}px)
								</Text>
								<Group position='apart' align='center' mb={8}>
									<Text size='sm' color='dimmed'>
										Crop format
									</Text>
									<NativeSelect
										size='sm'
										value={cropPresetKey}
										onChange={(e) => {
											setCropPresetKey(e.target.value)
											if (error) clearErrors()
										}}
										disabled={uploading}
										data={ITEM_IMAGE_CROP_PRESET_OPTIONS.map((preset) => ({
											value: preset.key,
											label: preset.label,
										}))}
										style={{ maxWidth: 220 }}
									/>
								</Group>
								<FileInput
									value={selectedImageFile}
									accept='image/png,image/jpeg,image/webp'
									onChange={handleFileChange}
									disabled={uploading}
									error={fieldErrors.image}
									placeholder='Choose image'
								/>
								{preview ? (
									<>
										<Group position='apart' align='center' mt='sm' mb={4}>
											<Text size='sm' color='dimmed'>
												{isRecropAllowed
													? 'Click image to re-crop'
													: 'Saved image: Upload a new image to modify'}
											</Text>
											<Button
												variant='subtle'
												size='xs'
												onClick={handleRemoveImage}
												disabled={uploading}
											>
												Remove image
											</Button>
										</Group>
										<Box
											component='img'
											src={preview}
											alt='Preview'
											onClick={
												isRecropAllowed ? handleRecropPreview : undefined
											}
											sx={{
												width: '100%',
												aspectRatio: `${activeCropPreset.width} / ${activeCropPreset.height}`,
												objectFit: 'cover',
												marginTop: 10,
												borderRadius: 8,
												cursor: isRecropAllowed ? 'pointer' : 'default',
											}}
										/>
									</>
								) : null}
								{!preview && item?.imageUrl ? (
									<Text size='sm' color='dimmed' mt='sm'>
										Image will be removed when you save.
									</Text>
								) : null}
							</Box>
						</Stack>
					</MantineTabs.Panel>

					<MantineTabs.Panel value='inventory'>
						<Stack spacing='md'>
							<Checkbox
								label='Allow this item to be added to check-ins'
								checked={allowAddToCheckIn}
								onChange={(e) => {
									const nextValue = e.target.checked
									setAllowAddToCheckIn(nextValue)
									if (!nextValue) setShowInExperienceStore(false)
									if (error) clearErrors()
								}}
							/>

							{allowAddToCheckIn ? (
								<TextInput
									label='List Price'
									icon='$'
									type='text'
									inputMode='decimal'
									placeholder='e.g. 15.00'
									value={listPrice}
									error={fieldErrors.listPrice}
									onChange={(e) => {
										setListPrice(e.target.value)
										if (error) clearErrors()
									}}
								/>
							) : null}

							<Checkbox
								label='Show in Experience Store'
								checked={showInExperienceStore}
								disabled={!allowAddToCheckIn}
								onChange={(e) => {
									const nextValue = e.target.checked
									const parsedPrice = Number.parseFloat(listPrice)
									if (
										nextValue &&
										(!Number.isFinite(parsedPrice) || parsedPrice < 0.5)
									) {
										setModalError(
											'Add a price of at least $0.50 before showing this item in the store.',
											{ listPrice: 'Enter a price of at least $0.50.' },
										)
										return
									}
									setShowInExperienceStore(nextValue)
									if (error) clearErrors()
								}}
							/>
							<Text size='xs' color='dimmed' mt={-8}>
								Visible items can be purchased from the public store.
							</Text>

							{showInExperienceStore ? (
								<Stack spacing='md'>
									<Textarea
										label='Experience Store Description'
										description='Optional description shown on the public store.'
										placeholder='Describe this item for travelers'
										minRows={3}
										maxLength={500}
										value={experienceStoreDescription}
										onChange={(event) => {
											setExperienceStoreDescription(event.target.value)
											if (error) clearErrors()
										}}
									/>

									<Box>
										<Text size='sm' weight={500} mb={6}>
											Experience Store Image ({EXPERIENCE_STORE_IMAGE_WIDTH}px
											 by {EXPERIENCE_STORE_IMAGE_HEIGHT}px)
										</Text>

										<FileInput
											value={selectedExperienceStoreImageFile}
											accept='image/png,image/jpeg,image/webp'
											onChange={handleExperienceStoreImageChange}
											disabled={uploading}
											error={fieldErrors.experienceStoreImage}
											placeholder='Choose image'
										/>
										{experienceStoreImagePreview ? (
											<>
												<Group position='apart' align='center' mt='sm' mb={4}>
													<Text size='sm' color='dimmed'>
														{isExperienceStoreRecropAllowed
															? 'Click image to re-crop'
															: 'Saved image: Upload a new image to modify'}
													</Text>
													<Button
														variant='subtle'
														size='xs'
														onClick={handleRemoveExperienceStoreImage}
														disabled={uploading}
													>
														Remove image
													</Button>
												</Group>
												<Box
													component='img'
													src={experienceStoreImagePreview}
													alt='Experience store preview'
													onClick={
														isExperienceStoreRecropAllowed
															? handleRecropExperienceStoreImage
															: undefined
													}
													sx={{
														width: '100%',
														aspectRatio: '16 / 9',
														objectFit: 'cover',
														marginTop: 10,
														borderRadius: 8,
														cursor: isExperienceStoreRecropAllowed
															? 'pointer'
															: 'default',
													}}
												/>
											</>
										) : null}
										{!experienceStoreImagePreview &&
										item?.experienceStoreImage ? (
											<Text size='sm' color='dimmed' mt='sm'>
												Image will be removed when you save.
											</Text>
										) : null}
									</Box>
								</Stack>
							) : null}

							<TextInput
								label='Total Unit Limit'
								type='number'
								min={0}
								step={1}
								inputMode='numeric'
								placeholder='Unlimited'
								value={totalUnitLimit}
								description='Leave blank for unlimited inventory.'
								error={fieldErrors.totalUnitLimit}
								onChange={(e) => {
									setTotalUnitLimit(e.target.value)
									if (error) clearErrors()
								}}
							/>
						</Stack>
					</MantineTabs.Panel>
				</MantineTabs>

				<Group position='right' spacing='sm'>
					<Button variant='default' onClick={onClose} disabled={uploading}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={uploading}>
						{uploading ? 'Uploading...' : item ? 'Save Changes' : 'Add'}
					</Button>
				</Group>
			</Stack>

			<ImageCropperModal
				opened={cropModalOpened}
				onClose={clearCropState}
				title={
					cropTarget === 'experienceStore'
						? 'Crop Experience Store Image'
						: 'Crop Item Image'
				}
				sourceUrl={cropSourceUrl}
				targetWidth={
					cropTarget === 'experienceStore'
						? EXPERIENCE_STORE_IMAGE_WIDTH
						: activeCropPreset.width
				}
				targetHeight={
					cropTarget === 'experienceStore'
						? EXPERIENCE_STORE_IMAGE_HEIGHT
						: activeCropPreset.height
				}
				onApply={handleApplyCrop}
				applyLabel='Use Cropped Image'
				zIndex={4000}
				closeOnClickOutside={false}
				closeOnEscape={false}
			/>
		</Modal>
	)
}

export default ItemOptionModal
